import {
  KillChainCategory,
  StudyMissionCapabilityGapPriority,
  StudyMissionCapabilityGapStatus,
  type StudyMissionSelectDto,
  type StudySelectDto,
  type StudyThreadSelectDto,
} from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Tile } from '@components/shared/tile.component';
import { UnsavedChangesModal } from '@components/shared/unsaved-changes-modal.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useDeleteMeiaStudyMission,
  useGetMeiaStudyMissionThreads,
  useUpdateMeiaStudyMission,
} from '@queries/meia.query';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { type MeiaMissionFormValues, meiaMissionSchema } from '@schemas/meia.schema';
import { useStore } from '@tanstack/react-form';
import { createFormInvalidHandler } from '@utils/form.util';
import { getStudyThreadKillChainLabel } from '@utils/meia-enum.util';
import { ArrowRightIcon, ChevronDownIcon, Trash2Icon } from 'lucide-react';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { MeiaInnerSection } from './meia-inner-section.component';
import {
  getMeiaMissionAreaOfOperations,
  getMeiaMissionAssumptionsConstraints,
  getMeiaMissionAuthoritiesRoeEnvelope,
  getMeiaMissionBlueForceComposition,
  getMeiaMissionCommandersIntent,
  getMeiaMissionDesiredOutcome,
  getMeiaMissionDetailRows,
  getMeiaMissionName,
  getMeiaMissionTimeWindow,
  sortMeiaMissionsByCreatedAt,
} from './meia-mission-display.util';
import { MeiaMissionMeasuresSection } from './meia-mission-measures-section.component';
import { MeiaMissionModal } from './meia-mission-modal.component';
import { MeiaMissionScopeStrip } from './meia-mission-scope-strip.component';
import { MeiaRedForceActivitiesTile } from './meia-red-force-activities-tile.component';
import { type MeiaTemplateOption, MeiaTemplatePopover } from './meia-template-popover.component';
import { getStudyThreadRoleBadgeVariant, getStudyThreadRoleLabel } from './meia-thread-role.util';

type MeiaMissionFramingEditorProps = {
  canEdit: boolean;
  missions: StudyMissionSelectDto[];
  onCreateMissionActionConsumed?: () => void;
  shouldOpenCreateMission?: boolean;
  study: StudySelectDto;
};

const missionCapabilityGapPriorityOptions = [
  { label: 'High', value: StudyMissionCapabilityGapPriority.HIGH },
  { label: 'Medium', value: StudyMissionCapabilityGapPriority.MEDIUM },
  { label: 'Low', value: StudyMissionCapabilityGapPriority.LOW },
];

const missionCapabilityGapStatusOptions = [
  { label: 'Open', value: StudyMissionCapabilityGapStatus.OPEN },
  { label: 'Investigating', value: StudyMissionCapabilityGapStatus.INVESTIGATING },
  { label: 'Answered', value: StudyMissionCapabilityGapStatus.ANSWERED },
];

const missionTypeCategoryOptions = Object.values(KillChainCategory).map((value) => ({
  label: getStudyThreadKillChainLabel(value),
  value,
}));

const prototypeCapabilityGapTemplates: MeiaTemplateOption[] = [
  {
    id: 'm-015-a',
    name: 'What capability gaps prevent suppression of forward A2/AD fires within the 90-minute joint-fires window?',
  },
  {
    id: 'm-015-b',
    name: 'How does suppressed-SAM window duration affect SAG mission-kill probability?',
  },
  {
    id: 'm-014-a',
    name: 'What capability gaps prevent zero-impact perimeter defense against Group-3 UAS over 96 h?',
  },
  {
    id: 'm-014-b',
    name: 'How does upstream UAS C2 disruption translate to defeated-on-perimeter rate?',
  },
  {
    id: 'm-013-a',
    name: 'Is sustainment posture sufficient to hold 80% intermodal throughput for 21 days under contested conditions?',
  },
  {
    id: 'm-013-b',
    name: 'What C2 design requirements emerge from surviving 6-hour SATCOM blackouts?',
  },
  {
    id: 'm-012-a',
    name: 'What is the operational impact if cyber access TTL drops below 30 days?',
  },
  {
    id: 'm-010-a',
    name: 'Where does F2T2EA latency exceed the 12-minute TST decision window?',
  },
];

const MissionDetailGrid: FC<{ mission: StudyMissionSelectDto }> = (props) => {
  const { mission } = props;

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {getMeiaMissionDetailRows(mission).map((row) => (
        <section
          className="flex min-w-0 flex-col gap-1 rounded-md border border-gray-200 px-2.5 py-2"
          key={row.label}
        >
          <h4 className="text-[11px] font-medium leading-tight text-gray-500">{row.label}</h4>
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-gray-700">
            {row.value.trim() ? row.value : <span className="text-gray-400">-</span>}
          </p>
        </section>
      ))}
    </div>
  );
};

const MissionThreadsPreview: FC<{
  missionId: string;
  studyId: string;
  threads: StudyThreadSelectDto[];
}> = (props) => {
  const { missionId, studyId, threads } = props;
  const navigate = useNavigate();

  return (
    <MeiaInnerSection
      action={
        <IconButton
          ariaLabel="Design threads"
          className="opacity-0 transition-opacity group-hover:opacity-100"
          icon={<ArrowRightIcon />}
          onClick={() => navigate(meiaRoute.studyTab({ query: { missionId }, studyId, tab: 'p2' }))}
          size="sm"
          title="Design threads"
          variant="secondary"
        />
      }
      title="Threads inside this mission"
    >
      <div className="flex flex-wrap gap-2">
        {threads.map((thread) => (
          <span
            key={thread.id}
            className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs text-gray-700"
          >
            <span>{getStudyThreadKillChainLabel(thread.killChainCategory)}</span>
            <span>{thread.name}</span>
            <Badge
              size="xs"
              variant={getStudyThreadRoleBadgeVariant(thread.role)}
            >
              {getStudyThreadRoleLabel(thread.role)}
            </Badge>
          </span>
        ))}
        {threads.length === 0 && (
          <span className="text-xs italic text-gray-400">No threads yet — add some in Phase 2.</span>
        )}
      </div>
    </MeiaInnerSection>
  );
};

type MissionFramingFormProps = {
  children?: ReactNode;
  mission: StudyMissionSelectDto;
  studyId: string;
};

const MissionFramingForm: FC<MissionFramingFormProps> = (props) => {
  const { children, mission, studyId } = props;
  const formId = `mission-framing-form-${mission.id}`;
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { isPending: isUpdating, mutateAsync: updateMission } = useUpdateMeiaStudyMission();
  const { mutateAsync: deleteMission } = useDeleteMeiaStudyMission();

  const defaultValues: MeiaMissionFormValues = {
    capabilityGaps: (mission.capabilityGaps ?? [])
      .toSorted((first, second) => first.position - second.position)
      .map((gap, index) => ({
        id: gap.id,
        position: index,
        priority: gap.priority,
        question: gap.question,
        sourcePromptRef: gap.sourcePromptRef ?? null,
        status: gap.status,
      })),
    missionContext: {
      areaOfOperations: getMeiaMissionAreaOfOperations(mission),
      assumptionsConstraints: getMeiaMissionAssumptionsConstraints(mission),
      authoritiesRoeEnvelope: getMeiaMissionAuthoritiesRoeEnvelope(mission),
      blueForceComposition: getMeiaMissionBlueForceComposition(mission),
      commandersIntent: getMeiaMissionCommandersIntent(mission),
      desiredMissionOutcome: getMeiaMissionDesiredOutcome(mission),
      timeWindow: getMeiaMissionTimeWindow(mission),
    },
    missionTypeCategory: mission.missionTypeCategory ?? null,
    name: getMeiaMissionName(mission),
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validationResult = meiaMissionSchema.safeParse(value);
      if (!validationResult.success) {
        errorToast({ description: 'Please complete required mission fields.' });
        return;
      }

      const payload = {
        ...validationResult.data,
        capabilityGaps: validationResult.data.capabilityGaps.map((gap, index) => ({
          ...gap,
          position: index,
        })),
      };

      await updateMission({
        ...payload,
        missionId: mission.id,
        studyId,
      });
      successToast({ description: 'Mission updated.' });
      // Saved values become the new pristine baseline so navigation is no longer blocked.
      form.reset(value);
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: { onSubmit: meiaMissionSchema },
  });

  // Mission switches (missionId search param) and study tab changes both run through
  // the router, so one blocker guards every way of leaving a dirty mission form.
  const isFormDirty = useStore(form.store, (state) => state.isDirty);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isFormDirty &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  );

  const handleBlockerDiscard = (): void => {
    form.reset();
    blocker.proceed?.();
  };

  const handleBlockerSaveAndContinue = async (): Promise<void> => {
    try {
      await form.handleSubmit();
    } catch {
      blocker.reset?.();
      return;
    }
    if (form.state.isDirty) {
      // Validation failed — stay on the page so the user can fix the form.
      blocker.reset?.();
      return;
    }
    blocker.proceed?.();
  };

  const handleConfirmDelete = async (): Promise<void> => {
    await deleteMission({ missionId: mission.id, studyId });
    successToast({ description: 'Mission deleted.' });
  };

  return (
    <div className="flex flex-col gap-4">
      <form
        {...MEIA_AUTOFILL_IGNORE_PROPS}
        className="flex flex-col gap-4"
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <MeiaInnerSection title="Mission Purpose & Investigative Frame">
            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  {...MEIA_AUTOFILL_IGNORE_PROPS}
                  label="Mission Name"
                  placeholder="Suppress forward A2/AD fires"
                  required
                />
              )}
            </form.AppField>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <form.AppField name="missionContext.desiredMissionOutcome">
                {(field) => (
                  <field.TextAreaField
                    label="Desired Mission Outcome (DMO)"
                    placeholder="Suppress long-range SAM coverage across 6 forward batteries for 90 minutes."
                    required
                    rows={3}
                  />
                )}
              </form.AppField>
              <form.AppField name="missionContext.commandersIntent">
                {(field) => (
                  <field.TextAreaField
                    label="Commander's Intent"
                    placeholder="Create a kinetic opening long enough for Maritime Strike to close on the SAG."
                    required
                    rows={3}
                  />
                )}
              </form.AppField>
            </div>
            <form.AppField name="missionTypeCategory">
              {(field) => (
                <field.SelectField
                  clearable
                  label="Mission Type"
                  options={missionTypeCategoryOptions}
                  placeholder="Select mission type"
                />
              )}
            </form.AppField>
          </MeiaInnerSection>

          <MeiaInnerSection
            description="Mission-specific — each mission can set different AO / authorities / window."
            title="Operating Parameters"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <form.AppField name="missionContext.areaOfOperations">
                {(field) => (
                  <field.TextAreaField
                    label="Area of Operations"
                    placeholder="Adversary coastal SAM belt, 80 nm littoral band, first-island-chain south."
                    required
                    rows={3}
                  />
                )}
              </form.AppField>
              <form.AppField name="missionContext.timeWindow">
                {(field) => (
                  <field.TextAreaField
                    label="Time Window"
                    placeholder="T+0 through T+90 min, 2026-07-02 0300Z-0430Z."
                    required
                    rows={3}
                  />
                )}
              </form.AppField>
              <form.AppField name="missionContext.blueForceComposition">
                {(field) => (
                  <field.TextAreaField
                    label="Blue Force Composition"
                    placeholder="2x F-35A / 2x EA-18G package plus organic HARM and Maven BDA feed."
                    rows={2}
                  />
                )}
              </form.AppField>
              <form.AppField name="missionContext.authoritiesRoeEnvelope">
                {(field) => (
                  <field.TextAreaField
                    label="Authorities / ROE Envelope"
                    placeholder="Positive ID required; no cross-border action."
                    rows={2}
                  />
                )}
              </form.AppField>
            </div>
            <form.AppField name="missionContext.assumptionsConstraints">
              {(field) => (
                <field.TextAreaField
                  label="Assumptions & Constraints"
                  placeholder="IADS posture matches latest composite intel update; no mobilization spike inside window."
                  rows={2}
                />
              )}
            </form.AppField>
          </MeiaInnerSection>

          <form.AppField
            mode="array"
            name="capabilityGaps"
          >
            {(gapArrayField) => {
              const capabilityGaps = gapArrayField.state.value ?? [];

              return (
                <MeiaInnerSection
                  title={
                    <span className="inline-flex items-center gap-1.5">
                      Capability Gaps ({capabilityGaps.length})
                      <TooltipComponent side="top">
                        Add the missing capabilities as questions the mission must answer.
                      </TooltipComponent>
                    </span>
                  }
                >
                  {capabilityGaps.map((gap, gapIndex) => (
                    <div
                      className="rounded-md bg-gray-100 p-2"
                      key={gap.id ?? `capability-gap-${gapIndex}`}
                    >
                      <div className="rounded-md bg-white">
                        <AccordionPrimitive.Root
                          collapsible
                          defaultValue="open"
                          type="single"
                        >
                          <AccordionPrimitive.Item value="open">
                            <div className="flex items-center justify-between gap-2 px-3 py-2">
                              <div className="flex items-center gap-2">
                                <AccordionPrimitive.Trigger className="group flex items-center gap-2 text-xs font-semibold text-gray-500 transition-colors hover:text-gray-700">
                                  <ChevronDownIcon className="size-3 shrink-0 text-gray-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                                  GQ #{gapIndex + 1}
                                </AccordionPrimitive.Trigger>
                                {gap.sourcePromptRef && (
                                  <Badge
                                    size="xs"
                                    variant="primary"
                                  >
                                    src: {gap.sourcePromptRef}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <MeiaTemplatePopover
                                  onSelect={(template) => {
                                    form.setFieldValue(`capabilityGaps[${gapIndex}].question`, template.name);
                                    form.setFieldValue(`capabilityGaps[${gapIndex}].sourcePromptRef`, template.id);
                                  }}
                                  renderTemplate={(template) => (
                                    <span className="text-sm text-gray-700">{template.name}</span>
                                  )}
                                  templates={prototypeCapabilityGapTemplates}
                                />
                                <IconButton
                                  ariaLabel={`Remove capability gap ${gapIndex + 1}`}
                                  icon={<Trash2Icon />}
                                  onClick={() => gapArrayField.removeValue(gapIndex)}
                                  variant="destructive"
                                />
                              </div>
                            </div>

                            <AccordionPrimitive.Content className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                              <div className="flex flex-col gap-3 p-3 pt-0">
                                <form.AppField name={`capabilityGaps[${gapIndex}].question`}>
                                  {(field) => (
                                    <field.TextAreaField
                                      label="Capability Gap Question"
                                      placeholder="What capability is missing, underperforming, or unproven for this mission?"
                                      required
                                      rows={2}
                                    />
                                  )}
                                </form.AppField>

                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                  <form.AppField name={`capabilityGaps[${gapIndex}].priority`}>
                                    {(field) => (
                                      <field.SelectField
                                        label="Priority"
                                        options={missionCapabilityGapPriorityOptions}
                                      />
                                    )}
                                  </form.AppField>
                                  <form.AppField name={`capabilityGaps[${gapIndex}].status`}>
                                    {(field) => (
                                      <field.SelectField
                                        label="Status"
                                        options={missionCapabilityGapStatusOptions}
                                      />
                                    )}
                                  </form.AppField>
                                </div>
                              </div>
                            </AccordionPrimitive.Content>
                          </AccordionPrimitive.Item>
                        </AccordionPrimitive.Root>
                      </div>
                    </div>
                  ))}

                  <Button
                    className="self-start"
                    onClick={() =>
                      gapArrayField.pushValue({
                        position: capabilityGaps.length,
                        priority: StudyMissionCapabilityGapPriority.MEDIUM,
                        question: '',
                        sourcePromptRef: null,
                        status: StudyMissionCapabilityGapStatus.OPEN,
                      })
                    }
                    type="button"
                    variant="link"
                  >
                    + Add Capability Gap
                  </Button>
                </MeiaInnerSection>
              );
            }}
          </form.AppField>
        </form.AppForm>
      </form>

      {children}

      <div className="sticky bottom-0 z-0 -mx-4 -mb-4 flex items-center justify-between gap-2 rounded-b-xl border-t border-gray-200 bg-white px-4 py-3">
        <Button
          onClick={() => setIsConfirmingDelete(true)}
          type="button"
          variant="destructive"
        >
          <Trash2Icon className="size-4" />
          Delete Mission
        </Button>
        <Button
          disabled={isUpdating}
          form={formId}
          type="submit"
        >
          {isUpdating ? 'Saving...' : 'Save Mission'}
        </Button>
      </div>

      <ConfirmDeleteModal
        isOpen={isConfirmingDelete}
        itemName="mission"
        onConfirm={handleConfirmDelete}
        setIsOpen={setIsConfirmingDelete}
      />

      <UnsavedChangesModal
        isOpen={blocker.state === 'blocked'}
        isSaving={isUpdating}
        onCancel={() => blocker.reset?.()}
        onDiscard={handleBlockerDiscard}
        onSaveAndContinue={() => void handleBlockerSaveAndContinue()}
      />
    </div>
  );
};

export const MeiaMissionFramingEditor: FC<MeiaMissionFramingEditorProps> = (props) => {
  const { canEdit, missions, onCreateMissionActionConsumed, shouldOpenCreateMission, study } = props;
  const studyId = study.id;
  const [isCreating, setIsCreating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const orderedMissions = useMemo(() => sortMeiaMissionsByCreatedAt(missions), [missions]);
  const { threads } = useGetMeiaStudyMissionThreads({ missions: orderedMissions, studyId });
  const requestedMissionId = searchParams.get('missionId');
  const activeMission =
    orderedMissions.find((mission) => mission.id === requestedMissionId) ?? orderedMissions[0] ?? null;
  const activeMissionThreads = useMemo(
    () => threads.filter((thread) => thread.missionId === activeMission?.id),
    [activeMission?.id, threads],
  );

  useEffect(() => {
    if (!canEdit || !shouldOpenCreateMission) return;
    setIsCreating(true);
    onCreateMissionActionConsumed?.();
  }, [canEdit, onCreateMissionActionConsumed, shouldOpenCreateMission]);

  const handleSelectMission = (missionId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('missionId', missionId);
    setSearchParams(next, { preventScrollReset: true, replace: true });
  };

  const headerButton = canEdit ? { onClick: () => setIsCreating(true), text: '+ Add Mission' } : undefined;

  const emptyState = canEdit
    ? { message: 'Add Mission', onClick: () => setIsCreating(true), title: 'No Missions' }
    : { title: 'No Missions' };

  const trailingSections = activeMission ? (
    <>
      <MeiaRedForceActivitiesTile
        canEdit={canEdit}
        mission={activeMission}
        studyId={studyId}
      />
      <MeiaMissionMeasuresSection
        canEdit={canEdit}
        missionId={activeMission.id}
      />
      <MissionThreadsPreview
        missionId={activeMission.id}
        studyId={studyId}
        threads={activeMissionThreads}
      />
    </>
  ) : null;

  return (
    <>
      <MeiaMissionScopeStrip
        emptyState={emptyState}
        headerButton={headerButton}
        missions={orderedMissions}
        onSelectMission={handleSelectMission}
        phase="p1"
        selectedMissionId={activeMission?.id}
        study={study}
        threads={threads}
        titleLabel="Missions"
      />

      {activeMission && (
        <Tile
          className="min-h-64"
          contentClassName="gap-4"
          header={{
            title: (
              <span className="inline-flex items-center gap-2">
                <span>{getMeiaMissionName(activeMission)}</span>
                <span className="text-xs font-normal text-gray-400">
                  ({activeMissionThreads.length} {activeMissionThreads.length === 1 ? 'thread' : 'threads'})
                </span>
              </span>
            ),
          }}
        >
          {canEdit ? (
            <MissionFramingForm
              key={activeMission.id}
              mission={activeMission}
              studyId={studyId}
            >
              {trailingSections}
            </MissionFramingForm>
          ) : (
            <div className="flex flex-col gap-4">
              <MissionDetailGrid mission={activeMission} />
              {trailingSections}
            </div>
          )}
        </Tile>
      )}

      {canEdit && isCreating && (
        <MeiaMissionModal
          isOpen={isCreating}
          onClose={() => setIsCreating(false)}
          onMissionCreated={handleSelectMission}
          studyId={studyId}
        />
      )}
    </>
  );
};
